import {
  COPILOT_CONVERSATIONS_STORAGE_KEY,
  COPILOT_MAX_CONVERSATIONS,
  COPILOT_MAX_MESSAGES_PER_CONVERSATION,
  COPILOT_THREAD_STORAGE_KEY
} from './copilot.constants';
import type {
  CopilotConversationTab,
  CopilotConversationsStore,
  CopilotUiMessage
} from './copilot.types';

export const COPILOT_UNTITLED_CONVERSATION = 'New chat';

export function createEmptyConversation(
  title = COPILOT_UNTITLED_CONVERSATION,
): CopilotConversationTab {
  return {
    id: newConversationId(),
    title,
    updatedAt: new Date().toISOString(),
    messages: [],
  };
}

export function newConversationId(): string {
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Drop pending flags and bulky debug payloads before persisting. */
export function sanitizeMessageForStorage(message: CopilotUiMessage): CopilotUiMessage {
  const { sourceData, pending, ...rest } = message;
  // Drop bulky debug payloads / pending flags before persisting.
  void sourceData;
  void pending;
  return { ...rest, pending: false };
}

export function trimMessagesForStorage(messages: CopilotUiMessage[]): CopilotUiMessage[] {
  const cleaned = messages
    .filter((m) => !m.pending)
    .map(sanitizeMessageForStorage);
  if (cleaned.length <= COPILOT_MAX_MESSAGES_PER_CONVERSATION) {
    return cleaned;
  }
  return cleaned.slice(cleaned.length - COPILOT_MAX_MESSAGES_PER_CONVERSATION);
}

export function deriveConversationTitle(
  messages: CopilotUiMessage[],
  fallback = COPILOT_UNTITLED_CONVERSATION,
): string {
  const firstUser = messages.find((m) => m.role === 'user' && !m.transient && m.content?.trim());
  if (!firstUser) {
    return fallback;
  }
  let text = firstUser.content
    .trim()
    .replace(/@\[([^\]]+)\]\([^)]+\)/g, '$1') // gateway mention → label
    .replace(/@(\S+)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) {
    return fallback;
  }
  // Keep a crisp tab label.
  if (text.length > 36) {
    text = `${text.slice(0, 35).trimEnd()}…`;
  }
  return text;
}

function isConversationTab(value: unknown): value is CopilotConversationTab {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const tab = value as Partial<CopilotConversationTab>;
  return typeof tab.id === 'string'
    && typeof tab.title === 'string'
    && typeof tab.updatedAt === 'string'
    && Array.isArray(tab.messages);
}

/** Load conversations from localStorage, migrating the legacy single-thread key when needed. */
export function loadCopilotConversationsStore(): CopilotConversationsStore {
  try {
    const raw = localStorage.getItem(COPILOT_CONVERSATIONS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<CopilotConversationsStore>;
      const conversations = Array.isArray(parsed.conversations)
        ? parsed.conversations.filter(isConversationTab).slice(0, COPILOT_MAX_CONVERSATIONS)
        : [];
      if (conversations.length > 0) {
        const activeId = conversations.some((c) => c.id === parsed.activeId)
          ? (parsed.activeId )
          : conversations[0].id;
        return {
          activeId,
          conversations: conversations.map((c) => ({
            ...c,
            messages: trimMessagesForStorage(c.messages ?? []),
          })),
        };
      }
    }
  } catch {
    // fall through to migration / empty
  }

  const legacyThread = localStorage.getItem(COPILOT_THREAD_STORAGE_KEY)?.trim() || undefined;
  const seed = createEmptyConversation();
  if (legacyThread) {
    seed.conversationId = legacyThread;
  }
  return { activeId: seed.id, conversations: [seed] };
}

export function saveCopilotConversationsStore(store: CopilotConversationsStore): void {
  const conversations = store.conversations
    .slice(0, COPILOT_MAX_CONVERSATIONS)
    .map((c) => ({
      ...c,
      messages: trimMessagesForStorage(c.messages ?? []),
    }));
  const activeId = conversations.some((c) => c.id === store.activeId)
    ? store.activeId
    : conversations[0]?.id ?? '';
  const payload: CopilotConversationsStore = { activeId, conversations };
  localStorage.setItem(COPILOT_CONVERSATIONS_STORAGE_KEY, JSON.stringify(payload));
  localStorage.removeItem(COPILOT_THREAD_STORAGE_KEY);
}

export function clearCopilotConversationsStore(): void {
  localStorage.removeItem(COPILOT_CONVERSATIONS_STORAGE_KEY);
  localStorage.removeItem(COPILOT_THREAD_STORAGE_KEY);
}
